import { useEffect, useState } from "react";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { useAuth } from "@/contexts/AuthContext";
import { COLLECTIONS, SUBCOLLECTIONS } from "@/integrations/firebase/collections";
import { logError } from "@/shared/services/ErrorReporting";
import { db } from "@/integrations/firebase/firestore";
import type { Notification } from "./types";

export interface UseNotificationsResult {
  notifications: Notification[];
  loading: boolean;
  error: Error | null;
}

export function useNotifications(): UseNotificationsResult {
  const { user, isImpersonating } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Reset before re-subscribing — see the note in `useRequests`. A stale
    // error outlives the subscription that set it, so a *successful*
    // snapshot repopulates the data while the consumer still renders the
    // failure, until a full reload. This hook is keyed on `user / isImpersonating`.
    setError(null);
    // The one identity-scoped read that cannot follow the impersonation
    // target: `users/{userId}/notifications` is own-doc-only in the rules
    // (no super-admin branch), so aiming this at `actingUid` would be denied.
    // Showing the *admin's* inbox under the target's name is exactly the
    // confusion to avoid, so a session has no bell at all — see the
    // NotificationBell, which hides itself. Granting
    // super admins a cross-user notification read is a privacy decision of
    // its own, and nobody has made it.
    if (!user || isImpersonating) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(
        db,
        COLLECTIONS.USERS,
        user.uid,
        SUBCOLLECTIONS.NOTIFICATIONS,
      ),
      orderBy("createdAt", "desc"),
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const next: Notification[] = snapshot.docs.map((d) => ({
          ...(d.data() as Omit<Notification, "id">),
          id: d.id,
        }));
        setNotifications(next);
        setLoading(false);
      },
      (err) => {
        logError(err);
        setError(err);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [user, isImpersonating]);

  return { notifications, loading, error };
}
